<template>
  <div ref="root">

    
  <!-- Hero section -->
  <section class="bg-white mt-[3.9rem] md:mt-14">
    <div class="mx-auto max-w-6xl px-6 lg:px-[60px] py-16 lg:py-20 flex flex-col gap-6 lg:gap-12">

      <!-- Back button -->
      <div>
        <router-link
          to="/"
          class="inline-flex items-center gap-2 px-3 py-1.5 border-[1.5px] border-[var(--color-neutral-300)] dark:border-[var(--color-neutral-600)] rounded-lg text-sm font-medium text-[var(--text-secondary)] hover:bg-[var(--surface-raised)] transition-colors duration-[var(--duration-normal)]">
          ← {{ $t('caseStudies.CS_strategic-design.backToHome') }}
        </router-link>
      </div>

      <!-- Title + Subtitle -->
      <div class="flex flex-col lg:flex-row items-start justify-between gap-8">
        <div class="lg:w-1/2">
          <h1 class="text-4xl sm:text-5xl font-bold leading-tight tracking-tight text-[var(--text-primary)]">
            {{ $t('caseStudies.CS_strategic-design.title') }}
          </h1>
        </div>
        <div class="lg:w-1/2">
          <p class="text-2xl font-medium leading-tight text-[var(--color-neutral-500)] dark:text-[var(--color-neutral-400)]">
            {{ $t('caseStudies.CS_strategic-design.title_description') }}
          </p>
        </div>
      </div>

      <!-- Metadata -->
      <div class="flex flex-wrap gap-x-16 gap-y-4">
        <!-- Company -->
        <div class="flex flex-col leading-[1.7]">
          <span class="text-sm text-[var(--color-neutral-500)] dark:text-[var(--color-neutral-400)]">{{ $t('caseStudies.CS_strategic-design.company.title') }}</span>
          <span class="text-base text-[var(--text-primary)]">{{ $t('caseStudies.CS_strategic-design.company.name') }}</span>
        </div>
        <!-- My role -->
        <div class="flex flex-col leading-[1.7]">
          <span class="text-sm text-[var(--color-neutral-500)] dark:text-[var(--color-neutral-400)]">{{ $t('caseStudies.CS_strategic-design.myRole.title') }}</span>
          <span class="text-base text-[var(--text-primary)]">{{ $t('caseStudies.CS_strategic-design.myRole.description') }}</span>
        </div>
        <!-- Team -->
        <div class="flex flex-col leading-[1.7]">
          <span class="text-sm text-[var(--color-neutral-500)] dark:text-[var(--color-neutral-400)]">{{ $tm('caseStudies.CS_strategic-design.myTeam')[0]?.title }}</span>
          <p v-for="(member, idx) in $tm('caseStudies.CS_strategic-design.myTeam')" :key="idx" class="text-base text-[var(--text-primary)] leading-[1.7] m-0">{{ member.description }}</p>
        </div>
        <!-- Timeline / Period -->
        <div class="flex flex-col leading-[1.7]" v-if="$tm('caseStudies.CS_strategic-design.timelines').length">
          <span class="text-sm text-[var(--color-neutral-500)] dark:text-[var(--color-neutral-400)]">{{ $tm('caseStudies.CS_strategic-design.timelines')[0].title }}</span>
          <span class="text-base text-[var(--text-primary)]">{{ $tm('caseStudies.CS_strategic-design.timelines')[0].description }}</span>
        </div>
      </div>

    </div>
  </section>
  <!-- End of Hero section -->


  <!-- Hero Graphic section -->
  <section class="bg-[var(--color-neutral-0)]">
    <div class="mx-auto max-w-6xl px-6 lg:px-[60px] py-16 flex flex-col gap-4">

      <!-- Main gradient hero -->
      <div
        class="w-full aspect-square md:aspect-auto overflow-hidden rounded-xl flex items-center justify-center"
        style="background: linear-gradient(8.84deg, rgba(255,255,255,0.7) 0%, rgba(26,152,255,0.7) 257.26%, rgba(255,255,255,0.7) 287.2%), linear-gradient(147.23deg, rgba(255,255,255,0.85) 3.23%, rgba(82,60,162,0.85) 190.64%)"
      >
        <img
          :src="heroComp01Src"
          alt="Amazon Learn manager dashboard on display"
          class="w-full h-full md:h-auto object-cover md:object-contain object-left"
        />
      </div>

      <!-- Shot panels -->
      <div class="flex flex-col sm:flex-row gap-4">
        <div class="bg-[#edf1ff] rounded-xl overflow-hidden sm:w-1/3 shrink-0 flex items-center justify-center p-2.5">
          <img
            :src="heroComp02Src"
            alt="Amazon Learn training card"
            class="w-full h-auto object-contain"
          />
        </div>
        <div class="bg-[#edf1ff] rounded-xl overflow-hidden flex-1 p-2.5">
          <img
            :src="heroComp03Src"
            alt="Amazon Learn training page"
            class="w-full h-auto object-contain"
          />
        </div>
      </div>

    </div>
  </section>
  <!-- End / Hero Graphic section -->


  <!-- Context section -->
  <section class="bg-[var(--color-neutral-0)] reveal">
    <div class="mx-auto max-w-6xl px-6 lg:px-[60px] py-12 lg:py-16 flex flex-col lg:flex-row items-center gap-16">
      <div class="flex flex-col gap-6 flex-1">
        <div class="flex flex-col gap-2">
          <p class="text-base font-medium leading-none text-[var(--color-neutral-500)] dark:text-[var(--color-neutral-400)]">
            {{ $t('caseStudies.CS_strategic-design.intro-section-title') }}
          </p>
          <h2 class="font-switzer text-3xl leading-tight sm:text-4xl sm:leading-snug font-bold text-[var(--text-primary)]">
            {{ $t('caseStudies.CS_strategic-design.intro-section-heading') }}
          </h2>
        </div>
        <p class="font-switzer text-lg font-normal leading-relaxed text-[var(--color-neutral-500)] dark:text-[var(--color-neutral-400)]">
          {{ $t('caseStudies.CS_strategic-design.intro-section') }}
        </p>
      </div>
      <img
        :src="amazonLearnLogoSrc"
        alt="Amazon Learn logo"
        class="h-[54px] lg:h-[72px] w-auto shrink-0 dark:brightness-0 dark:invert"
      />
    </div>
  </section>
  <!-- End / Context section -->


  <!-- Overview section -->
  <section class="bg-[#edf1ff] dark:bg-[var(--color-neutral-800)] reveal">
    <div class="mx-auto max-w-6xl px-6 lg:px-[60px] py-12 lg:py-24 flex flex-col lg:flex-row items-center gap-12">

      <!-- Text -->
      <div class="flex flex-col gap-4 lg:w-[550px] shrink-0">
        <p class="text-base font-medium leading-none text-[var(--color-neutral-500)] dark:text-[var(--color-neutral-400)]">
          {{ $t('caseStudies.CS_strategic-design.overview-section.label') }}
        </p>
        <h2 class="font-switzer text-3xl leading-tight sm:text-4xl sm:leading-snug font-bold text-[var(--text-primary)]">
          {{ $t('caseStudies.CS_strategic-design.overview-section.title') }}
        </h2>
        <p class="font-switzer text-lg font-normal leading-relaxed text-[var(--color-neutral-500)] dark:text-[var(--color-neutral-400)]">
          {{ $t('caseStudies.CS_strategic-design.overview-section.description') }}
        </p>
      </div>

      <!-- Video (lazy-loaded) / fallback image -->
      <div class="w-full min-w-0 max-w-sm mx-auto lg:max-w-none">
        <LazyVideo
          :src="overviewVideoSrc"
          :fallback-src="floatingCoursesSrc"
          fallback-alt="Amazon Learn course cards showing multiple strategies"
          class="w-full h-auto object-contain rounded-xl"
        />
      </div>

    </div>
  </section>
  <!-- End / Overview section -->


  <!-- Discovery section -->
  <section class="bg-[var(--color-neutral-0)] dark:bg-[var(--color-neutral-800)] reveal">
    <div class="mx-auto max-w-6xl px-6 lg:px-[60px] py-12 lg:py-24 flex flex-col gap-16">

      <!-- Header -->
      <div class="flex flex-col gap-6">
        <div class="flex flex-col gap-2">
          <p class="text-base font-medium leading-none text-[var(--color-neutral-500)] dark:text-[var(--color-neutral-400)]">
            {{ $t('caseStudies.CS_strategic-design.discovery-section.label') }}
          </p>
          <h2 class="font-switzer text-3xl leading-tight sm:text-4xl sm:leading-snug font-bold text-[var(--text-primary)]">
            {{ $t('caseStudies.CS_strategic-design.discovery-section.title') }}
          </h2>
        </div>
        <div class="flex flex-col gap-4">
          <p class="font-switzer text-xl font-normal leading-relaxed text-[var(--color-neutral-500)] dark:text-[var(--color-neutral-400)]" v-html="$t('caseStudies.CS_strategic-design.discovery-section.description')"></p>
          <p class="font-switzer text-xl font-normal leading-relaxed text-[var(--color-neutral-500)] dark:text-[var(--color-neutral-400)]">{{ $t('caseStudies.CS_strategic-design.discovery-section.description-2') }}</p>
        </div>
      </div>

      <!-- Cards -->
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">

        <div class="bg-[#edf1ff] dark:bg-[var(--color-neutral-700)] rounded-2xl border border-[var(--color-neutral-300)] p-6 flex flex-col gap-3">
          <span class="font-switzer text-2xl font-bold leading-snug text-[var(--color-neutral-500)] dark:text-[var(--color-neutral-400)]">{{ $tm('caseStudies.CS_strategic-design.discovery-section.cards')[0].title }}</span>
          <p class="font-switzer text-xl font-normal leading-relaxed text-[var(--color-neutral-500)] dark:text-[var(--color-neutral-400)]">
            {{ $tm('caseStudies.CS_strategic-design.discovery-section.cards')[0].description }}
          </p>
        </div>

        <div class="bg-[#edf1ff] dark:bg-[var(--color-neutral-700)] rounded-2xl border border-[var(--color-neutral-300)] p-6 flex flex-col gap-3">
          <span class="font-switzer text-2xl font-bold leading-snug text-[var(--color-neutral-500)] dark:text-[var(--color-neutral-400)]">{{ $tm('caseStudies.CS_strategic-design.discovery-section.cards')[1].title }}</span>
          <p class="font-switzer text-xl font-normal leading-relaxed text-[var(--color-neutral-500)] dark:text-[var(--color-neutral-400)]">
            {{ $tm('caseStudies.CS_strategic-design.discovery-section.cards')[1].description }}
          </p>
        </div>

        <div class="bg-[#edf1ff] dark:bg-[var(--color-neutral-700)] rounded-2xl border border-[var(--color-neutral-300)] p-6 flex flex-col gap-3">
          <span class="font-switzer text-2xl font-bold leading-snug text-[var(--color-neutral-500)] dark:text-[var(--color-neutral-400)]">{{ $tm('caseStudies.CS_strategic-design.discovery-section.cards')[2].title }}</span>
          <p class="font-switzer text-xl font-normal leading-relaxed text-[var(--color-neutral-500)] dark:text-[var(--color-neutral-400)]">
            {{ $tm('caseStudies.CS_strategic-design.discovery-section.cards')[2].description }}
          </p>
        </div>

      </div>

    </div>
  </section>
  <!-- End / Discovery section -->


  <!-- Concepts section -->
  <section class="bg-[var(--color-neutral-0)] dark:bg-[var(--color-neutral-800)] reveal">
    <div class="mx-auto max-w-6xl px-6 lg:px-[60px] py-12 flex flex-col gap-8">

      <!-- Header -->
      <div class="flex flex-col gap-6">
        <div class="flex flex-col gap-2">
          <p class="text-base font-medium leading-none text-[var(--color-neutral-500)] dark:text-[var(--color-neutral-400)]">
            {{ $t('caseStudies.CS_strategic-design.concepts-section.label') }}
          </p>
          <h2 class="font-switzer text-3xl leading-tight sm:text-4xl sm:leading-snug font-bold text-[var(--text-primary)]">
            {{ $t('caseStudies.CS_strategic-design.concepts-section.title') }}
          </h2>
        </div>
        <p class="font-switzer text-lg font-normal leading-relaxed text-[var(--color-neutral-500)] dark:text-[var(--color-neutral-400)]">
          {{ $t('caseStudies.CS_strategic-design.concepts-section.description') }}
        </p>
        <div class="font-switzer text-lg font-normal leading-relaxed text-[var(--color-neutral-500)] dark:text-[var(--color-neutral-400)]">
          <p class="font-bold text-[var(--text-primary)]">{{ $t('caseStudies.CS_strategic-design.concepts-section.list-title') }}</p>
          <ul class="list-disc list-inside mt-2 flex flex-col gap-1">
            <li v-for="(item, idx) in $tm('caseStudies.CS_strategic-design.concepts-section.list-items')" :key="idx">{{ item }}</li>
          </ul>
        </div>
      </div>

      <!-- Image block -->
      <div class="bg-[var(--color-neutral-100)] dark:bg-[var(--color-neutral-700)] rounded-2xl pt-4 px-4 pb-6 flex flex-col gap-6">
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
          <img
            :src="exploringOptions00Src"
            alt="Early exploration: training catalogue view"
            class="rounded-lg w-full h-auto object-cover"
            loading="lazy"
          />
          <img
            :src="exploringOptions01Src"
            alt="Early exploration: manager learning insights view"
            class="rounded-lg w-full h-auto object-cover"
            loading="lazy"
          />
        </div>
        <p class="text-sm text-center text-[var(--color-neutral-700)] dark:text-[var(--color-neutral-400)]">
          {{ $t('caseStudies.CS_strategic-design.concepts-section.image-caption') }}
        </p>
      </div>

    </div>
  </section>
  <!-- End / Concepts section -->


  <!-- Proposal intro section -->
  <section class="bg-[var(--color-neutral-0)] dark:bg-[var(--color-neutral-800)] reveal">
    <div class="mx-auto max-w-6xl px-6 lg:px-[60px] py-12 flex flex-col gap-6">
      <div class="flex flex-col gap-2">
        <p class="text-base font-medium leading-none text-[var(--color-neutral-500)] dark:text-[var(--color-neutral-400)]">
          {{ $t('caseStudies.CS_strategic-design.proposal-intro-section.label') }}
        </p>
        <h2 class="text-3xl leading-tight sm:text-4xl sm:leading-snug font-bold text-[var(--text-primary)]">
          {{ $t('caseStudies.CS_strategic-design.proposal-intro-section.title') }}
        </h2>
      </div>
      <p class="font-switzer text-lg font-normal leading-relaxed text-[var(--color-neutral-500)] dark:text-[var(--color-neutral-400)]">
        {{ $t('caseStudies.CS_strategic-design.proposal-intro-section.description') }}
      </p>
    </div>
  </section>
  <!-- End / Proposal intro section -->


  <!-- Proposal graphics section -->
  <section class="bg-[var(--color-neutral-0)]">
    
    <!-- video 00 - Manager view -->
    <div class="mx-auto max-w-6xl px-6 lg:px-[60px] py-12 lg:py-16 flex flex-col-reverse lg:flex-row items-start gap-8">

      <!-- Video -->
      <div class="w-full lg:flex-[3] min-w-0">
        <div
          class="rounded-xl p-2 flex items-center justify-center"
          style="background: linear-gradient(10.21deg, rgba(255,255,255,0.7) 0%, rgba(26,152,255,0.7) 257.26%, rgba(255,255,255,0.7) 287.2%), linear-gradient(143.31deg, rgba(255,255,255,0.85) 3.23%, rgba(82,60,162,0.85) 190.64%)"
        >
          <LazyVideo
            :src="proposalVideoSrc"
            :fallback-src="floatingCoursesSrc"
            fallback-alt="Amazon Learn team training visibility feature"
            class="w-full h-auto object-contain rounded-xl"
          />
        </div>
      </div>

      <!-- Text -->
      <div class="w-full lg:flex-1 min-w-0 flex flex-col gap-4 lg:pt-4">
        <h3 class="font-switzer text-2xl font-medium leading-snug text-[var(--text-primary)]">
          {{ $t('caseStudies.CS_strategic-design.proposal-graphics-section.title') }}
        </h3>
        <p class="font-switzer text-base font-normal leading-relaxed text-[var(--color-neutral-500)] dark:text-[var(--color-neutral-400)]">
          {{ $t('caseStudies.CS_strategic-design.proposal-graphics-section.description') }}
        </p>
      </div>

    </div>
    <!-- End / video 00 - Manager view -->

    <!-- video 01 - Learner view -->
    <div class="mx-auto max-w-6xl px-6 lg:px-[60px] py-12 lg:py-16 flex flex-col-reverse lg:flex-row items-start gap-8">

      <!-- Video -->
      <div class="w-full lg:flex-[3] min-w-0">
        <div
          class="rounded-xl p-2 flex items-center justify-center"
          style="background: linear-gradient(10.21deg, rgba(255,255,255,0.7) 0%, rgba(26,152,255,0.7) 257.26%, rgba(255,255,255,0.7) 287.2%), linear-gradient(143.31deg, rgba(255,255,255,0.85) 3.23%, rgba(82,60,162,0.85) 190.64%)"
        >
          <LazyVideo
            :src="learnerVideoSrc"
            :fallback-src="floatingCoursesSrc"
            fallback-alt="Amazon Learn learner dashboard showing training agenda"
            class="w-full h-auto object-contain rounded-xl"
          />
        </div>
      </div>

      <!-- Text -->
      <div class="w-full lg:flex-1 min-w-0 flex flex-col gap-4 lg:pt-4">
        <h3 class="font-switzer text-2xl font-medium leading-snug text-[var(--text-primary)]">
          {{ $t('caseStudies.CS_strategic-design.proposal-graphics-section-2.title') }}
        </h3>
        <p class="font-switzer text-base font-normal leading-relaxed text-[var(--color-neutral-500)] dark:text-[var(--color-neutral-400)]">
          {{ $t('caseStudies.CS_strategic-design.proposal-graphics-section-2.description') }}
        </p>
        <p class="font-switzer text-base font-normal leading-relaxed text-[var(--color-neutral-500)] dark:text-[var(--color-neutral-400)]">
          {{ $t('caseStudies.CS_strategic-design.proposal-graphics-section-2.description-2') }}
        </p>
      </div>

    </div>
    <!-- End / video 01 - Learner view -->

    <!-- video 02 - Exploring trainings -->
    <div class="mx-auto max-w-6xl px-6 lg:px-[60px] py-12 lg:py-16 flex flex-col-reverse lg:flex-row items-start gap-8">

      <!-- Video -->
      <div class="w-full lg:flex-[3] min-w-0">
        <div
          class="rounded-xl p-2 flex items-center justify-center"
          style="background: linear-gradient(10.21deg, rgba(255,255,255,0.7) 0%, rgba(26,152,255,0.7) 257.26%, rgba(255,255,255,0.7) 287.2%), linear-gradient(143.31deg, rgba(255,255,255,0.85) 3.23%, rgba(82,60,162,0.85) 190.64%)"
        >
          <LazyVideo
            :src="exploringTrainingsVideoSrc"
            :fallback-src="floatingCoursesSrc"
            fallback-alt="Amazon Learn training catalogue with search and recommendations"
            class="w-full h-auto object-contain rounded-xl"
          />
        </div>
      </div>

      <!-- Text -->
      <div class="w-full lg:flex-1 min-w-0 flex flex-col gap-4 lg:pt-4">
        <h3 class="font-switzer text-2xl font-medium leading-snug text-[var(--text-primary)]">
          {{ $t('caseStudies.CS_strategic-design.proposal-graphics-section-3.title') }}
        </h3>
        <p class="font-switzer text-base font-normal leading-relaxed text-[var(--color-neutral-500)] dark:text-[var(--color-neutral-400)]">
          {{ $t('caseStudies.CS_strategic-design.proposal-graphics-section-3.description') }}
        </p>
      </div>

    </div>
    <!-- End / video 02 - Exploring trainings -->
  </section>
  <!-- End / Proposal graphics section -->


  <!-- Results section -->
  <section class="bg-[var(--surface-raised)] reveal">
    <div class="mx-auto max-w-6xl px-6 lg:px-[60px] py-16 lg:py-20 flex flex-col gap-12">

      <!-- Header -->
      <div class="flex flex-col gap-6">
        <div class="flex flex-col gap-2">
          <p class="text-xs font-medium leading-none tracking-[0.78px] uppercase text-[var(--color-neutral-500)] dark:text-[var(--color-neutral-400)]">
            {{ $t('caseStudies.CS_strategic-design.results-section.label') }}
          </p>
          <h2 class="text-3xl leading-tight sm:text-4xl sm:leading-snug font-bold text-[var(--text-primary)]">
            {{ $t('caseStudies.CS_strategic-design.results-section.title') }}
          </h2>
        </div>
        <p class="font-switzer text-lg font-normal leading-relaxed text-[var(--color-neutral-500)] dark:text-[var(--color-neutral-400)] max-w-[840px]">
          {{ $t('caseStudies.CS_strategic-design.results-section.description') }}
        </p>
      </div>

      <!-- Stats -->
      <div class="flex flex-col sm:flex-row gap-8 lg:gap-12">
        <div
          v-for="(stat, idx) in $tm('caseStudies.CS_strategic-design.results-section.stats')"
          :key="idx"
          class="flex flex-col gap-1"
        >
          <p class="font-switzer text-2xl sm:text-3xl font-medium leading-snug text-[var(--text-primary)]">{{ stat.value }}</p>
          <p class="font-switzer text-lg font-normal leading-relaxed text-[var(--color-neutral-500)] dark:text-[var(--color-neutral-400)]">{{ stat.label }}</p>
        </div>
      </div>

    </div>
  </section>
  <!-- End / Results section -->

  <!-- Selected work section -->
  <section class="bg-[var(--surface-bg)]">
    <div class="mx-auto max-w-6xl px-6 lg:px-[60px] py-16 flex flex-col gap-6">

      <!-- Header + divider -->
      <div class="flex flex-col gap-4">
        <div class="flex flex-col gap-2">
          <p class="text-xs font-medium leading-none tracking-[0.06em] uppercase text-[var(--color-neutral-500)]">
            {{ $t('caseStudies.CS_strategic-design.selected-work-section.label') }}
          </p>
          <h3 class="text-2xl sm:text-3xl font-medium leading-snug text-[var(--text-primary)]">
            {{ $t('caseStudies.CS_strategic-design.selected-work-section.title') }}
          </h3>
        </div>
      </div>

      <!-- Link buttons -->
      <div class="flex flex-wrap gap-8">
        <router-link
          v-for="(link, idx) in $tm('caseStudies.CS_strategic-design.selected-work-section.links')"
          :key="idx"
          :to="{ name: link.name }"
          class="inline-flex items-center justify-center h-9 px-5 rounded-md border border-[var(--color-brand-primary)] text-[var(--color-brand-primary)] text-xs font-medium leading-none whitespace-nowrap transition-colors duration-[var(--duration-normal)] hover:bg-[var(--color-brand-primary-light)] dark:hover:bg-[var(--surface-brand)]"
        >
          {{ link.label }}
        </router-link>
      </div>

    </div>
  </section>
  <!-- End / Selected work section -->




  </div>
</template>

<script>
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import LazyVideo from '../components/LazyVideo.vue'
import { useScrollReveal } from '../composables/useScrollReveal'

export default {
  name: "Contact-me",
  components: {
    LazyVideo
  },
  methods: {
    isExternal(url) {
      // crude check: external if starts with http.
      // Used to determine what type of button to use (router-link vs link) .
      return /^https?:\/\//.test(url);
    }
  },
  computed: {
    // Computed properties are cached getters, not functions.
    // so we return a function to use parameters such fileName.
    imgSrc() {
      return (urlFile) => {
        try {
          return require(`../${urlFile}`);
        } catch (e) {
          return require("../assets/img-placeholder.jpg");
        }
      }
    },

    iconSrc() {
      return (urlIcon) => {
        try {
          return require(`../${urlIcon}`);
        } catch (e) {
          return require("../assets/img-placeholder.jpg");
        }
      }
    }


  },
  setup() {
    const { t } = useI18n()
    const root = ref(null)
    useScrollReveal(root)

    const heroComp01Src = require('../assets/work-gallery/CS-design-strategy/CS_Hero-composition-01.png');
    const heroComp02Src = require('../assets/work-gallery/CS-design-strategy/CS_Hero-composition-02.png');
    const heroComp03Src = require('../assets/work-gallery/CS-design-strategy/CS_Hero-composition-03.png');
    const amazonLearnLogoSrc = require('../assets/customers/AmazonLearn.svg');
    const floatingCoursesSrc = require('../assets/work-gallery/CS-design-strategy/floating_courses.png');
    const exploringOptions00Src = require('../assets/work-gallery/CS-design-strategy/CS_strategy_exploring-options_00.png');
    const exploringOptions01Src = require('../assets/work-gallery/CS-design-strategy/CS_strategy_exploring-options_01.png');
    const overviewVideoSrc = require('../assets/work-gallery/CS-design-strategy/CS_strategy-many-strategies.mp4')
    const proposalVideoSrc = require('../assets/work-gallery/CS-design-strategy/CS_strategy-adding-trainings.mp4')
    const learnerVideoSrc = require('../assets/work-gallery/CS-design-strategy/CS_strategy-learner-view.mp4')
    const exploringTrainingsVideoSrc = require('../assets/work-gallery/CS-design-strategy/CS_strategy-exploring-trainings.mp4')

    return {t, root, heroComp01Src, heroComp02Src, heroComp03Src, amazonLearnLogoSrc, floatingCoursesSrc, overviewVideoSrc, exploringOptions00Src, exploringOptions01Src, proposalVideoSrc, learnerVideoSrc, exploringTrainingsVideoSrc};
  },
};
</script>
<style scoped>
.reveal {
  opacity: 0;
  transform: translateY(44px);
  transition: opacity 0.6s ease, transform 0.6s ease;
}

.reveal.visible {
  opacity: 1;
  transform: translateY(0);
}

@media (prefers-reduced-motion: reduce) {
  .reveal {
    opacity: 1;
    transform: none;
    transition: none;
  }
}
</style>
